// ui/particle-layer.js: M5 Change #5.
//
// Thin canvas particle system (no library, synthesis §5 calls out
// pixi.js as overkill). Two burst shapes ship: chip scatter (#10
// trick win) and fire upward (#15 loner sweep). End-of-hand (#12) and
// score-label (#12b) reuse BURST with team colors.
//
// Layer contract:
//   • Lives on its own overlay canvas (`<canvas id="particle-layer" />`)
//     above the game canvas, sized to match. `pointer-events: none`
//     so clicks fall through to the game.
//   • One rAF loop owned by the layer, independent of the game's
//     render loop. The game can stutter without breaking particle
//     timing.
//   • All burst types respect reduced-motion: when active, the layer
//     forces count=0 so nothing emits.

import { lerp } from './easing.js';

/** @typedef {{ x:number, y:number, vx:number, vy:number, ax:number, ay:number,
 *              ageMs:number, lifeMs:number, color:string, w:number, h:number,
 *              shape:'square'|'rect' }} Particle */

/** @typedef {{ count?: number, color?: string, durationMs?: number }} BurstOpts */

/**
 * @param {{ reducedMotion?: boolean, now?: () => number }} [opts]
 */
export function createParticleLayer(opts = {}) {
  const nowFn = opts.now || (() => (typeof performance !== 'undefined' && performance.now ? performance.now() : Date.now()));
  let reducedMotion = !!opts.reducedMotion;
  /** @type {Particle[]} */
  const particles = [];
  let lastTickMs = null;
  /** @type {HTMLCanvasElement | null} */
  let canvas = null;
  /** @type {number | null} */
  let rafId = null;
  let running = false;

  function setReducedMotion(v) { reducedMotion = !!v; }
  function isReducedMotion() { return reducedMotion; }

  function attach(targetCanvas) {
    canvas = targetCanvas;
    if (!running) startLoop();
  }

  function detach() {
    canvas = null;
    stopLoop();
    particles.length = 0;
    lastTickMs = null;
  }

  function startLoop() {
    if (running) return;
    running = true;
    const tick = () => {
      if (!running) return;
      const now = nowFn();
      const dt = lastTickMs === null ? 0 : (now - lastTickMs);
      lastTickMs = now;
      step(dt);
      if (canvas) draw(canvas);
      if (typeof requestAnimationFrame === 'function') {
        rafId = requestAnimationFrame(tick);
      } else {
        // jsdom / Node tests: no rAF, caller drives via tickOnce.
        rafId = null;
        running = false;
      }
    };
    if (typeof requestAnimationFrame === 'function') {
      rafId = requestAnimationFrame(tick);
    }
  }

  function stopLoop() {
    running = false;
    if (rafId !== null && typeof cancelAnimationFrame === 'function') {
      cancelAnimationFrame(rafId);
    }
    rafId = null;
  }

  /**
   * One simulation step. Exposed for tests so we can drive the layer
   * without rAF.
   *
   * @param {number} dtMs
   */
  function step(dtMs) {
    if (dtMs <= 0) return;
    const dtSec = dtMs / 1000;
    let writeIdx = 0;
    for (let i = 0; i < particles.length; i++) {
      const p = particles[i];
      p.ageMs += dtMs;
      if (p.ageMs >= p.lifeMs) continue;
      p.vx += p.ax * dtSec;
      p.vy += p.ay * dtSec;
      p.x += p.vx * dtSec;
      p.y += p.vy * dtSec;
      particles[writeIdx++] = p;
    }
    particles.length = writeIdx;
  }

  function tickOnce(dtMs) {
    step(dtMs);
    if (canvas) draw(canvas);
  }

  /**
   * Spawn a burst at the given origin.
   *
   * @param {'BURST'|'FIRE'} type
   * @param {{x:number, y:number}} origin
   * @param {BurstOpts} [burstOpts]
   * @returns {number} number of particles emitted
   */
  function burst(type, origin, burstOpts = {}) {
    if (reducedMotion) return 0;
    if (!origin || typeof origin.x !== 'number' || typeof origin.y !== 'number') return 0;
    const def = (type === 'FIRE') ? fireDef(burstOpts) : burstDef(burstOpts);
    const count = def.count;
    for (let i = 0; i < count; i++) {
      const angle = def.angleFn(i, count);
      const speed = def.speedFn();
      particles.push({
        x: origin.x,
        y: origin.y,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed,
        ax: def.ax,
        ay: def.ay,
        ageMs: 0,
        lifeMs: def.lifeMs,
        color: def.colorFn(),
        w: def.w,
        h: def.h,
        shape: def.shape,
      });
    }
    return count;
  }

  function burstDef(o) {
    return {
      count: typeof o.count === 'number' ? o.count : 6,
      lifeMs: typeof o.durationMs === 'number' ? o.durationMs : 600,
      // Chips arc UP-and-OUT: angle in upper hemisphere, speed 80-140 px/s.
      angleFn: (i, n) => {
        // Spread between -150° and -30° (upper half) so they fly up.
        const t = n <= 1 ? 0.5 : i / (n - 1);
        return -Math.PI + lerp(Math.PI * 5/6, Math.PI / 6, t);
      },
      speedFn: () => 80 + Math.random() * 60,
      ax: 0,
      ay: -60,            // upward float, y decreases on canvas, so negative
      colorFn: () => o.color || '#F0C040',
      w: 4,
      h: 4,
      shape: /** @type {'square'} */ ('square'),
    };
  }

  function fireDef(o) {
    return {
      count: typeof o.count === 'number' ? o.count : 10,
      lifeMs: typeof o.durationMs === 'number' ? o.durationMs : 800,
      // Fire goes mostly STRAIGHT UP with x jitter.
      angleFn: () => -Math.PI / 2 + (Math.random() - 0.5) * (Math.PI / 4),
      speedFn: () => 60 + Math.random() * 40,
      ax: 0,
      ay: -40,
      colorFn: () => fireColor(),
      w: 6,
      h: 8,
      shape: /** @type {'rect'} */ ('rect'),
    };
  }

  function fireColor() {
    // Random pick across the fire gradient (orange → red).
    const r = Math.random();
    if (r < 0.5) return '#F0A030';        // orange
    if (r < 0.85) return '#E07028';       // amber
    return '#E03048';                     // red
  }

  function clear() {
    particles.length = 0;
  }

  function count() {
    return particles.length;
  }

  /**
   * @param {HTMLCanvasElement} target
   */
  function draw(target) {
    const ctx = target.getContext('2d');
    if (!ctx) return;
    ctx.clearRect(0, 0, target.width, target.height);
    for (const p of particles) {
      const lifeT = p.ageMs / p.lifeMs;
      const alpha = Math.max(0, 1 - lifeT);
      ctx.globalAlpha = alpha;
      ctx.fillStyle = p.color;
      if (p.shape === 'square') {
        ctx.fillRect(Math.round(p.x - p.w / 2), Math.round(p.y - p.h / 2), p.w, p.h);
      } else {
        ctx.fillRect(Math.round(p.x - p.w / 2), Math.round(p.y - p.h / 2), p.w, p.h);
      }
    }
    ctx.globalAlpha = 1;
  }

  return {
    attach, detach,
    burst, clear, count,
    step, tickOnce, draw,
    setReducedMotion, isReducedMotion,
  };
}
